/**
 * WB4.5 — AI suggest pilot: domain → registry action id SSOT (palette · inspector parity).
 * Execution surface requirements: `action-surface-parity-matrix.ts` (ACTION_SURFACE_PARITY_MATRIX).
 *
 * Parity-enrolled domains auto-derive pilot ids from top-5 ∩ matrix `ai` surface.
 * Manual overrides cover proxy domains outside the parity pilot list.
 */

import { resolveActionSurfaceParityRequirements } from './action-surface-parity-matrix';
import {
  REFERENCE_DOMAINS,
  resolveDomainActionFamily,
  resolveDomainActionParityPilotDomains,
  resolveDomainTop5ActionIds,
} from './domain-action-rule-table';
import { SELLABLE_TOP_5_ACTION_IDS } from './sellable-action-rule-table';
import { resolveWorkbenchGrammarProxy } from './workbench-domain-grammar-proxy';

/** Canonical action ids allowed on the AI suggest execution surface per domain. */
export const AI_SUGGEST_REFERENCE_ACTION_IDS = ['view-related-records'] as const;

/** Domains outside parity auto-enrollment that still expose AI suggest pilots. */
const AI_SUGGEST_MANUAL_DOMAIN_OVERRIDES: Readonly<Record<string, readonly string[]>> = {
  sales_order: ['start_order_workflow'],
  document_report: [...AI_SUGGEST_REFERENCE_ACTION_IDS],
  calendar_anchor: [...AI_SUGGEST_REFERENCE_ACTION_IDS],
  flow_definition: [...AI_SUGGEST_REFERENCE_ACTION_IDS],
  stream_definition: [...AI_SUGGEST_REFERENCE_ACTION_IDS],
  task_execution: [...AI_SUGGEST_REFERENCE_ACTION_IDS],
};

function resolveTop5ActionIdsForDomain(businessDomain: string): readonly string[] | undefined {
  if (businessDomain === 'sellable_item') {
    return SELLABLE_TOP_5_ACTION_IDS;
  }
  return resolveDomainTop5ActionIds(businessDomain) ?? undefined;
}

/** Action ids requiring the ai execution surface for a parity-enrolled domain. */
export function resolveAiSuggestPilotActionIdsForDomain(businessDomain: string): readonly string[] {
  const topIds = resolveTop5ActionIdsForDomain(businessDomain);
  if (!topIds) {
    return [];
  }
  return topIds.filter((actionId) =>
    resolveActionSurfaceParityRequirements(actionId)?.includes('ai')
  );
}

function buildAiSuggestPilotActionIdsByDomain(): Readonly<Record<string, readonly string[]>> {
  const map: Record<string, readonly string[]> = { ...AI_SUGGEST_MANUAL_DOMAIN_OVERRIDES };

  for (const domain of resolveDomainActionParityPilotDomains()) {
    const derived = resolveAiSuggestPilotActionIdsForDomain(domain);
    if (derived.length > 0) {
      map[domain] = derived;
    }
  }

  return map;
}

/** Pilot domains whose suggest API may return these canonical action ids. */
export const AI_SUGGEST_PILOT_ACTION_IDS_BY_DOMAIN: Readonly<Record<string, readonly string[]>> =
  buildAiSuggestPilotActionIdsByDomain();

const REFERENCE_DOMAIN_SET = new Set<string>(REFERENCE_DOMAINS);

const OBJECT_DOMAIN_LINE = /object\s+([^\s/]+)\//;

/** Parses `object {domain}/{id}` line from assistive gateway sourceText. */
export function parseBusinessDomainFromAssistiveSourceText(
  sourceText?: string
): string | undefined {
  if (!sourceText?.trim()) {
    return undefined;
  }
  return OBJECT_DOMAIN_LINE.exec(sourceText)?.[1];
}

function resolveDirectAiSuggestActionIds(domain: string): readonly string[] {
  return AI_SUGGEST_PILOT_ACTION_IDS_BY_DOMAIN[domain] ?? [];
}

export function inferAssistiveSuggestedActionIds(input: {
  sourceText?: string;
  businessDomain?: string;
}): readonly string[] {
  const domain =
    input.businessDomain?.trim() || parseBusinessDomainFromAssistiveSourceText(input.sourceText);
  if (!domain) {
    return [];
  }

  const direct = resolveDirectAiSuggestActionIds(domain);
  if (direct.length > 0) {
    return direct;
  }

  if (REFERENCE_DOMAIN_SET.has(domain) || resolveDomainActionFamily(domain) === 'reference') {
    return AI_SUGGEST_REFERENCE_ACTION_IDS;
  }

  const proxy = resolveWorkbenchGrammarProxy(domain);
  if (proxy) {
    const fromCanonical = resolveDirectAiSuggestActionIds(proxy.canonicalBusinessDomain);
    if (fromCanonical.length > 0) {
      return fromCanonical;
    }
  }

  return [];
}

export interface AiPilotEnrollmentViolation {
  reason: string;
}

/** Fails when parity-enrolled domains with ai-required top-5 lack registry entries. */
export function collectAiPilotEnrollmentViolations(): AiPilotEnrollmentViolation[] {
  const violations: AiPilotEnrollmentViolation[] = [];

  for (const domain of resolveDomainActionParityPilotDomains()) {
    const required = resolveAiSuggestPilotActionIdsForDomain(domain);
    const registered = new Set(AI_SUGGEST_PILOT_ACTION_IDS_BY_DOMAIN[domain] ?? []);

    for (const actionId of required) {
      if (!registered.has(actionId)) {
        violations.push({
          reason: `AI pilot ${domain}: missing auto-enrolled action ${actionId}`,
        });
      }
    }
  }

  return violations;
}
